import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  avatar?: string;
  phone?: string;
  tenantId: string;
  permissions: string[];
  preferences?: {
    language?: string;
    timezone?: string;
    theme?: 'light' | 'dark' | 'system';
    notifications?: {
      email?: boolean;
      push?: boolean;
      sms?: boolean;
    };
  };
  calComLink?: string | null;
}

export interface Tenant {
  id: string;
  name: string;
  slug: string;
  logo?: string;
  industry?: string;
  businessType?: string;
  address?: {
    line1: string;
    line2?: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
  };
  contact?: {
    email?: string;
    phone?: string;
    website?: string;
  };
  settings?: {
    currency: string;
    timezone: string;
    dateFormat: string;
  };
  branding?: {
    primaryColor?: string;
    secondaryColor?: string;
    logoUrl?: string;
    faviconUrl?: string;
  };
}

interface AuthState {
  user: User | null;
  tenant: Tenant | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  // Actions
  setUser: (user: User | null) => void;
  setTenant: (tenant: Tenant | null) => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  login: (user: User, tenant: Tenant, accessToken: string, refreshToken: string) => void;
  logout: () => void;
  setLoading: (loading: boolean) => void;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      tenant: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: true,

      setUser: (user) => set({ user, isAuthenticated: !!user }),

      setTenant: (tenant) => set({ tenant }),

      setTokens: (accessToken, refreshToken) => set({ accessToken, refreshToken }),

      login: (user, tenant, accessToken, refreshToken) =>
        set({
          user,
          tenant,
          accessToken,
          refreshToken,
          isAuthenticated: true,
          isLoading: false,
        }),

      logout: () =>
        set({
          user: null,
          tenant: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
        }),

      setLoading: (isLoading) => set({ isLoading }),

      hasPermission: (permission) => {
        const { user } = get();
        if (!user) return false;
        if (user.role === 'super_admin' || user.role === 'tenant_owner') return true;
        return user.permissions.includes(permission);
      },

      hasAnyPermission: (permissions) => {
        const { user, hasPermission } = get();
        if (!user) return false;
        if (user.role === 'super_admin' || user.role === 'tenant_owner') return true;
        return permissions.some((p) => hasPermission(p));
      },

      hasAllPermissions: (permissions) => {
        const { user, hasPermission } = get();
        if (!user) return false;
        if (user.role === 'super_admin' || user.role === 'tenant_owner') return true;
        return permissions.every((p) => hasPermission(p));
      },
    }),
    {
      name: 'business-os-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        tenant: state.tenant,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
